import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { MedicationSupply } from '@wodi/domain';
import type { WardOrder } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import { verificationInError, verifyOrder, wardOrdersQuery } from '../api/emar.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { PharmacyNav } from '../pharmacy/common.tsx';
import styles from '../pharmacy/pharmacy.module.css';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Ward orders (docs/plans/phase2-emar-inpatient-pharmacy.md §5): the facility's active
// inpatient medication orders. The pharmacist verifies each and chooses how it is supplied:
// dispensed for the patient (the prescription screen) or from the ward's own stock.

const SUPPLY_OPTIONS = [
  {
    value: 'patient_supply',
    label: 'Patient supply',
    description: 'Dispensed for this patient from the pharmacy; charged when dispensed.',
  },
  {
    value: 'ward_stock',
    label: 'Ward stock',
    description:
      'Given from the ward’s own stock; each dose given takes stock and is charged then.',
  },
] as const;

function VerifyDialog({ order, onClose }: { order: WardOrder; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [supply, setSupply] = useState<MedicationSupply>('patient_supply');
  const save = useMutation({
    mutationFn: () => verifyOrder(order.lineId, { supply, expectedVersion: order.lineVersion }),
    onSuccess: (next) => {
      queryClient.setQueryData(wardOrdersQuery.queryKey, next);
      toasts.show({ title: 'Order verified' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Verify order"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        {`${order.drugDisplay}. ${order.doseDisplay}. For ${order.patient.displayName}.`}
      </p>
      <RadioGroup
        label="Supplied as"
        options={SUPPLY_OPTIONS.map((o) => ({
          id: o.value,
          label: o.label,
          description: o.description,
        }))}
        value={supply}
        onChange={(value) => {
          setSupply(value as MedicationSupply);
        }}
      />
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The order was not verified. Try again.')}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Verify order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InErrorDialog({ order, onClose }: { order: WardOrder; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const verificationId = order.verification?.id ?? '';
  const mark = useMutation({
    mutationFn: () => verificationInError(verificationId, reason.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(wardOrdersQuery.queryKey, next);
      toasts.show({ title: 'Verification entered in error' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Enter verification in error"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        The order goes back to "Not verified". A ward-stock order's balance can be dispensed again.
      </p>
      <TextArea label="Why is it in error?" value={reason} onChange={setReason} rows={2} />
      {mark.error === null ? null : (
        <Notice tone="error">{errorText(mark.error, 'Nothing was changed. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep verification
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < REASON_MIN_LENGTH}
          isPending={mark.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mark.mutate();
          }}
        >
          Enter in error
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function stateOf(order: WardOrder): { text: string; tag: 'pending' | 'done' | 'active' } {
  const v = order.verification;
  if (v === null) return { text: 'Not verified', tag: 'pending' };
  if (v.supply === 'ward_stock') return { text: 'Ward stock', tag: 'done' };
  return order.remaining > 0
    ? { text: 'To dispense', tag: 'active' }
    : { text: 'Dispensed', tag: 'done' };
}

export function PharmacyWardOrdersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const orders = useQuery(wardOrdersQuery);
  const [verifying, setVerifying] = useState<WardOrder | null>(null);
  const [marking, setMarking] = useState<WardOrder | null>(null);
  const mayVerify = session !== null && can(session, 'pharmacy.verify');
  const mayDispense = session !== null && can(session, 'pharmacy.dispense');

  return (
    <div className={styles.page}>
      <PageHeader
        title="Ward orders"
        description="Medicine orders for patients on the wards. Verify each, then dispense it for the patient or supply it from ward stock."
      />
      <PharmacyNav />
      {session === null || !can(session, 'pharmacy.read') ? (
        <Notice tone="info">
          You don't have permission to see ward orders. Ask an administrator if you need it.
        </Notice>
      ) : orders.isPending ? (
        <LoadingBlock label="Loading ward orders" rows={6} />
      ) : orders.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void orders.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(orders.error, "Ward orders couldn't be loaded. Try again.")}
        </Notice>
      ) : (
        <>
          {!online ? (
            <Notice tone="info">No connection to Wodi. Verifying waits for it.</Notice>
          ) : null}
          <Table flat dense aria-label="Ward orders">
            <TableHeader>
              <Column isRowHeader>Medicine</Column>
              <Column>Patient</Column>
              <Column>Ward</Column>
              <Column>Prescribed</Column>
              <Column>Dispensed</Column>
              <Column>State</Column>
              <Column>Actions</Column>
            </TableHeader>
            <TableBody
              items={orders.data.orders.map((o) => ({ ...o, id: o.lineId }))}
              renderEmptyState={() => 'No medicine orders on the wards.'}
            >
              {(order) => {
                const state = stateOf(order);
                return (
                  <Row id={order.lineId}>
                    <Cell>
                      <span>
                        {order.drugDisplay}
                        <span className={styles.meta}>{` ${order.doseDisplay}`}</span>
                        {order.isControlled ? <Tag state="attention">Controlled</Tag> : null}
                      </span>
                    </Cell>
                    <Cell>
                      <span>
                        {order.patient.displayName}{' '}
                        <span className="wodi-id">{order.patient.patientNumber}</span>
                      </span>
                    </Cell>
                    <Cell>
                      {order.ward === null
                        ? ''
                        : `${order.ward.name}${order.bedLabel === null ? '' : `, ${order.bedLabel}`}`}
                    </Cell>
                    <Cell>
                      <span
                        className={styles.meta}
                      >{`${formatInstant(order.signedAt)}, ${order.prescriberDisplay}`}</span>
                    </Cell>
                    <Cell>
                      <span
                        className={styles.numbers}
                      >{`${String(order.dispensed)} of ${String(order.quantityPrescribed)}`}</span>
                    </Cell>
                    <Cell>
                      <Tag state={state.tag}>{state.text}</Tag>
                    </Cell>
                    <Cell>
                      <span className={styles.actions}>
                        {order.verification === null && mayVerify ? (
                          <Button
                            dense
                            isDisabled={!online}
                            onPress={() => {
                              setVerifying(order);
                            }}
                          >
                            Verify
                          </Button>
                        ) : null}
                        {order.verification?.supply === 'patient_supply' &&
                        order.remaining > 0 &&
                        mayDispense ? (
                          <Link to="/pharmacy" search={{ prescriptionId: order.prescriptionId }}>
                            Dispense
                          </Link>
                        ) : null}
                        {order.verification !== null && mayVerify ? (
                          <Button
                            dense
                            variant="quiet"
                            isDisabled={!online}
                            onPress={() => {
                              setMarking(order);
                            }}
                          >
                            Enter in error
                          </Button>
                        ) : null}
                      </span>
                    </Cell>
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </>
      )}
      {verifying === null ? null : (
        <VerifyDialog
          order={verifying}
          onClose={() => {
            setVerifying(null);
          }}
        />
      )}
      {marking === null ? null : (
        <InErrorDialog
          order={marking}
          onClose={() => {
            setMarking(null);
          }}
        />
      )}
    </div>
  );
}
